import React, { useState } from 'react';
import {
    Card,
    Button,
    Space,
    Tag,
    Typography,
    Descriptions,
    Alert,
    Modal,
    Spin,
    message,
    Input
} from 'antd';
import {
    FileTextOutlined,
    BarcodeOutlined,
    SearchOutlined,
    EnvironmentOutlined,
    ClockCircleOutlined,
    DeleteOutlined
} from '@ant-design/icons';
import { useTranslation } from '../../hooks/useTranslation';
import { useSearchSfExpressRoutesByAppointmentId, useDeleteSfExpressOrder } from '../../hooks/useGraphQL';
import { SfExpressOrder, SfExpressRouteInfo } from '../../types/sf-express.types';
import { usePermissions } from '../../hooks/usePermissions';

const { Text } = Typography;
const { TextArea } = Input;



// Helper function to get localized View PDF button text
const getViewPdfButtonText = (locale: string): string => {
    switch (locale) {
        case 'zh-CN':
            return '查看顺丰PDF';
        case 'zh-HK':
            return '查看順豐PDF';
        default:
            return 'View SF Express PDF';
    }
};

// Helper function to get waybill status color
const getWaybillStatusColor = (status: string): string => {
    switch (status) {
        case 'created':
        case 'success':
            return 'green';
        case 'failed':
        case 'error':
            return 'red';
        case 'pending':
        case 'processing':
            return 'orange';
        default:
            return 'default';
    }
};

// Helper function to get localized waybill status text
const getLocalizedWaybillStatus = (status: string, t: (k: string) => string): string => {
    switch (status) {
        case 'created':
        case 'success':
            return t('sfExpress.status.created');
        case 'failed':
        case 'error':
            return t('sfExpress.status.failed');
        case 'pending':
        case 'processing':
            return t('sfExpress.status.processing');
        default:
            return status;
    }
};

// Helper function to format date with enhanced locale support
const formatDate = (dateString: string, locale: string): string => {
    try {
        const date = new Date(dateString);
        
        // Enhanced locale mapping for better date formatting
        const localeMap: { [key: string]: string } = {
            'en': 'en-US',
            'zh-CN': 'zh-CN',
            'zh-HK': 'zh-HK'
        };
        
        const mappedLocale = localeMap[locale] || 'en-US';
        
        return date.toLocaleDateString(mappedLocale, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    } catch {
        return dateString;
    }
};

// Helper function to get localized route status color
const getRouteStatusColor = (status: string): string => {
    // Handle Chinese status messages from SF Express
    if (status?.includes('已收取快件')) {
        return 'blue';
    }
    if (status?.includes('已签收')) {
        return 'green';
    }
    if (status?.includes('运输中') || status?.includes('派送中')) {
        return 'blue';
    }
    if (status?.includes('已发货')) {
        return 'blue';
    }
    if (status?.includes('待处理') || status?.includes('等待中')) {
        return 'orange';
    }
    if (status?.includes('异常') || status?.includes('失败')) {
        return 'red';
    }
    
    // Handle English status messages
    switch (status?.toLowerCase()) {
        case 'delivered':
        case 'completed':
            return 'green';
        case 'in_transit':
        case 'shipping':
            return 'blue';
        case 'pending':
        case 'processing':
            return 'orange';
        case 'failed':
        case 'error':
            return 'red';
        default:
            return 'default';
    }
};

// Helper function to get localized route status text
const getLocalizedRouteStatus = (status: string, t: (k: string) => string): string => {
    // Handle Chinese status messages from SF Express
    if (status?.includes('已收取快件')) {
        return t('sfExpress.routeStatus.pickedUp') || 'Package Picked Up';
    }
    if (status?.includes('已签收')) {
        return t('sfExpress.routeStatus.delivered') || 'Delivered';
    }
    if (status?.includes('运输中') || status?.includes('派送中')) {
        return t('sfExpress.routeStatus.inTransit') || 'In Transit';
    }
    if (status?.includes('已发货')) {
        return t('sfExpress.routeStatus.shipped') || 'Shipped';
    }
    if (status?.includes('待处理') || status?.includes('等待中')) {
        return t('sfExpress.routeStatus.pending') || 'Pending';
    }
    if (status?.includes('异常') || status?.includes('失败')) {
        return t('sfExpress.routeStatus.failed') || 'Failed';
    }
    
    // Handle English status messages
    switch (status?.toLowerCase()) {
        case 'delivered':
            return t('sfExpress.routeStatus.delivered') || 'Delivered';
        case 'in_transit':
            return t('sfExpress.routeStatus.inTransit') || 'In Transit';
        case 'shipping':
            return t('sfExpress.routeStatus.shipping') || 'Shipping';
        case 'pending':
            return t('sfExpress.routeStatus.pending') || 'Pending';
        case 'processing':
            return t('sfExpress.routeStatus.processing') || 'Processing';
        case 'failed':
            return t('sfExpress.routeStatus.failed') || 'Failed';
        default:
            return status || t('sfExpress.routeStatus.unknown') || 'Unknown';
    }
};

interface SfExpressCardSeparateProps {
    appointmentId?: string;
    onCreateSfExpressOrder: () => Promise<void>;
    creatingSfOrder: boolean;
    onViewSfExpressPdf?: (waybillNo: string) => void;
    onDeleteSfExpressOrder?: () => void;
    showSfExpressButton?: boolean;
    showRouteLookup?: boolean;
    style?: React.CSSProperties;
    locale?: string;
    sfExpressOrders?: SfExpressOrder[];
    title?: string;
    showTitle?: boolean;
}

const SfExpressCardSeparate: React.FC<SfExpressCardSeparateProps> = ({
    appointmentId,
    onCreateSfExpressOrder,
    creatingSfOrder,
    onViewSfExpressPdf,
    onDeleteSfExpressOrder,
    showSfExpressButton = true,
    showRouteLookup = true,
    style,
    locale,
    sfExpressOrders = [],
    title,
    showTitle = true
}) => {
    const { t, currentLanguage } = useTranslation();
    const { canDeleteAppointments } = usePermissions();
    const [routeModalVisible, setRouteModalVisible] = useState(false);
    const [routeInfo, setRouteInfo] = useState<SfExpressRouteInfo | null>(null);
    const [routeLoading, setRouteLoading] = useState(false);
    const [deleteModalVisible, setDeleteModalVisible] = useState(false);
    const [deleteReason, setDeleteReason] = useState('');

    // SF Express route search hook
    const [searchRoutes, { loading: searchLoading }] = useSearchSfExpressRoutesByAppointmentId();
    
    // SF Express delete hook
    const [deleteSfExpressOrder, { loading: deleteLoading }] = useDeleteSfExpressOrder();

    // Use provided locale or fall back to current language
    const activeLocale = locale || currentLanguage;
    
    // Enhanced locale detection and validation
    const isValidLocale = (loc: string): boolean => {
        return ['en', 'zh-CN', 'zh-HK'].includes(loc);
    };
    
    const validatedLocale = isValidLocale(activeLocale) ? activeLocale : 'en';

    // Check if there are any SF Express orders with waybill numbers
    const hasWaybill = sfExpressOrders.some(order => order.waybill_no);
    const latestOrder = sfExpressOrders.length > 0 ? sfExpressOrders[0] : null;


    const handleViewSfExpressPdf = () => {
        if (latestOrder?.waybill_no && onViewSfExpressPdf) {
            onViewSfExpressPdf(latestOrder.waybill_no);
        }
    };

    // Route lookup handlers
    const handleRouteLookup = async () => {
        if (!appointmentId) {
            message.error(t('sfExpress.noAppointmentId'));
            return;
        }

        setRouteLoading(true);
        setRouteModalVisible(true);

        try {
            const result = await searchRoutes({ variables: { appointmentId } });
            
            if (result.data?.searchSfExpressRoutesByAppointmentId?.success) {
                setRouteInfo(result.data.searchSfExpressRoutesByAppointmentId.data);
                message.success(t('sfExpress.routeLookupSuccess'));
            } else {
                const errorMsg = result.data?.searchSfExpressRoutesByAppointmentId?.errorMsg || 
                               t('sfExpress.routeLookupFailed');
                message.error(errorMsg);
                setRouteInfo(null);
            }
        } catch (error) {
            console.error('Route lookup error:', error);
            message.error(t('sfExpress.routeLookupError'));
            setRouteInfo(null);
        } finally {
            setRouteLoading(false);
        }
    };

    const handleCloseRouteModal = () => {
        setRouteModalVisible(false);
        setRouteInfo(null);
    };

    // Delete handlers
    const handleDeleteSfExpressOrder = () => {
        setDeleteModalVisible(true);
    };

    const handleConfirmDelete = async () => {
        if (!latestOrder?.request_id) {
            message.error(t('sfExpress.noOrderToDelete'));
            return;
        }

        try {
            await deleteSfExpressOrder({
                variables: {
                    input: {
                        orderId: latestOrder.request_id,
                        reason: deleteReason || undefined
                    }
                }
            });

            message.success(t('sfExpress.orderDeletedSuccessfully'));
            setDeleteModalVisible(false);
            setDeleteReason('');
            
            // Call the parent callback to refresh data
            if (onDeleteSfExpressOrder) {
                onDeleteSfExpressOrder();
            }
        } catch (error) {
            console.error('Error deleting SF Express order:', error);
            message.error(t('sfExpress.deleteOrderFailed'));
        }
    };

    const handleCancelDelete = () => {
        setDeleteModalVisible(false);
        setDeleteReason('');
    };

    // Don't render if no SF Express waybill exists
    if (!hasWaybill) {
        return null;
    }

    return (
        <>
            <Card
                title={showTitle ? (title || t('sfExpress.title')) : undefined}
                style={{ 
                    marginBottom: 0, 
                    ...style 
                }}
                extra={
                    <Space>
                        {hasWaybill && showRouteLookup && (
                            <Button
                                type="default"
                                icon={<SearchOutlined />}
                                size="small"
                                onClick={handleRouteLookup}
                                loading={routeLoading || searchLoading}
                                title={t('sfExpress.lookupRoute')}
                            >
                                {t('sfExpress.lookupRoute')}
                            </Button>
                        )}
                        {hasWaybill && canDeleteAppointments() && (
                            <Button
                                type="default"
                                danger
                                icon={<DeleteOutlined />}
                                size="small"
                                onClick={handleDeleteSfExpressOrder}
                                loading={deleteLoading}
                                title={t('sfExpress.deleteOrder')}
                            >
                                {t('sfExpress.deleteOrder')}
                            </Button>
                        )}
                        {hasWaybill && (
                            <Button
                                type="primary"
                                icon={<FileTextOutlined />}
                                size="small"
                                onClick={handleViewSfExpressPdf}
                                title={getViewPdfButtonText(validatedLocale)}
                            >
                                {getViewPdfButtonText(validatedLocale)}
                            </Button>
                        )}
                    </Space>
                }
            >
                {/* SF Express Waybill Information */}
                {hasWaybill && latestOrder && (
                    <div>
                        <div style={{
                            border: '1px solid #d9d9d9',
                            borderRadius: '6px',
                            padding: '16px',
                            backgroundColor: '#fafafa'
                        }}>
                            <div style={{ marginBottom: 12 }}>
                                <Space>
                                    <BarcodeOutlined style={{ color: '#1890ff' }} />
                                    <Text strong style={{ fontSize: '14px' }}>
                                        {t('sfExpress.waybillInfo')}
                                    </Text>
                                </Space>
                            </div>

                            <Descriptions column={1} size="small" bordered>
                                {latestOrder.waybill_no && (
                                    <Descriptions.Item
                                        label={
                                            <Space>
                                                <BarcodeOutlined />
                                                {t('sfExpress.waybillNo')}
                                            </Space>
                                        }
                                    >
                                        <Text code style={{ fontSize: '13px' }}>
                                            {latestOrder.waybill_no}
                                        </Text>
                                    </Descriptions.Item>
                                )}

                                {latestOrder.order_id && (
                                    <Descriptions.Item
                                        label={
                                            <Space>
                                                <BarcodeOutlined />
                                                {t('sfExpress.orderId')}
                                            </Space>
                                        }
                                    >
                                        <Text code style={{ fontSize: '13px' }}>
                                            {latestOrder.order_id}
                                        </Text>
                                    </Descriptions.Item>
                                )}

                                {latestOrder.tracking_number && (
                                    <Descriptions.Item
                                        label={
                                            <Space>
                                                <BarcodeOutlined />
                                                {t('sfExpress.trackingNumber')}
                                            </Space>
                                        }
                                    >
                                        <Text code style={{ fontSize: '13px' }}>
                                            {latestOrder.tracking_number}
                                        </Text>
                                    </Descriptions.Item>
                                )}

                                {latestOrder.status && (
                                    <Descriptions.Item
                                        label={
                                            <Space>
                                                <Tag color={getWaybillStatusColor(latestOrder.status)} />
                                                {t('sfExpress.status')}
                                            </Space>
                                        }
                                    >
                                        <Tag color={getWaybillStatusColor(latestOrder.status)}>
                                            {getLocalizedWaybillStatus(latestOrder.status, t)}
                                        </Tag>
                                    </Descriptions.Item>
                                )}

                                {latestOrder.created_at && (
                                    <Descriptions.Item
                                        label={
                                            <Space>
                                                <ClockCircleOutlined />
                                                {t('sfExpress.createdAt')}
                                            </Space>
                                        }
                                    >
                                        <Text>{formatDate(latestOrder.created_at, validatedLocale)}</Text>
                                    </Descriptions.Item>
                                )}
                            </Descriptions>

                            {latestOrder.error_message && (
                                <div style={{ marginTop: 12 }}>
                                    <Alert
                                        message={t('sfExpress.error')}
                                        description={latestOrder.error_message}
                                        type="error"
                                        showIcon
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                )}

            </Card>

            {/* Route Lookup Modal */}
            <Modal
                title={
                    <Space>
                        <SearchOutlined />
                        {t('sfExpress.routeLookupTitle')}
                    </Space>
                }
                open={routeModalVisible}
                onCancel={handleCloseRouteModal}
                footer={[
                    <Button key="close" onClick={handleCloseRouteModal}>
                        {t('common.close')}
                    </Button>
                ]}
                width={800}
                destroyOnClose
            >
                {routeLoading || searchLoading ? (
                    <div style={{ textAlign: 'center', padding: '40px' }}>
                        <Spin size="large" />
                        <div style={{ marginTop: 16 }}>
                            {t('sfExpress.loadingRouteInfo')}
                        </div>
                    </div>
                ) : routeInfo ? (
                    <div>
                        <Descriptions column={1} bordered size="small">
                            {routeInfo.waybillNo && (
                                <Descriptions.Item
                                    label={
                                        <Space>
                                            <BarcodeOutlined />
                                            {t('sfExpress.waybillNo')}
                                        </Space>
                                    }
                                >
                                    <Text code style={{ fontSize: '13px' }}>
                                        {routeInfo.waybillNo}
                                    </Text>
                                </Descriptions.Item>
                            )}

                            {routeInfo.trackingNumber && (
                                <Descriptions.Item
                                    label={
                                        <Space>
                                            <BarcodeOutlined />
                                            {t('sfExpress.trackingNumber')}
                                        </Space>
                                    }
                                >
                                    <Text code style={{ fontSize: '13px' }}>
                                        {routeInfo.trackingNumber}
                                    </Text>
                                </Descriptions.Item>
                            )}

                            {routeInfo.currentStatus && (
                                <Descriptions.Item
                                    label={
                                        <Space>
                                            <Tag color={getRouteStatusColor(routeInfo.currentStatus || '')} />
                                            {t('sfExpress.currentStatus')}
                                        </Space>
                                    }
                                >
                                    <Tag color={getRouteStatusColor(routeInfo.currentStatus || '')}>
                                        {getLocalizedRouteStatus(routeInfo.currentStatus || '', t)}
                                    </Tag>
                                </Descriptions.Item>
                            )}

                            {routeInfo.currentLocation && (
                                <Descriptions.Item
                                    label={
                                        <Space>
                                            <EnvironmentOutlined />
                                            {t('sfExpress.currentLocation')}
                                        </Space>
                                    }
                                >
                                    <Text>{routeInfo.currentLocation}</Text>
                                </Descriptions.Item>
                            )}

                            {routeInfo.estimatedDelivery && (
                                <Descriptions.Item
                                    label={
                                        <Space>
                                            <ClockCircleOutlined />
                                            {t('sfExpress.estimatedDelivery')}
                                        </Space>
                                    }
                                >
                                    <Text>{routeInfo.estimatedDelivery ? formatDate(routeInfo.estimatedDelivery, validatedLocale) : ''}</Text>
                                </Descriptions.Item>
                            )}

                            {routeInfo.lastUpdateTime && (
                                <Descriptions.Item
                                    label={
                                        <Space>
                                            <ClockCircleOutlined />
                                            {t('sfExpress.lastUpdateTime')}
                                        </Space>
                                    }
                                >
                                    <Text>{routeInfo.lastUpdateTime ? formatDate(routeInfo.lastUpdateTime, validatedLocale) : ''}</Text>
                                </Descriptions.Item>
                            )}

                            {routeInfo.routeInfo && (
                                <Descriptions.Item
                                    label={
                                        <Space>
                                            <SearchOutlined />
                                            {t('sfExpress.routeInfo')}
                                        </Space>
                                    }
                                >
                                    <div>
                                        {routeInfo.routeInfo.split('; ').map((routeEntry, index) => (
                                            <div key={index} style={{ 
                                                marginBottom: index < routeInfo.routeInfo!.split('; ').length - 1 ? 8 : 0,
                                                padding: '8px',
                                                backgroundColor: index === 0 ? '#f6ffed' : '#fafafa',
                                                border: index === 0 ? '1px solid #b7eb8f' : '1px solid #d9d9d9',
                                                borderRadius: '4px'
                                            }}>
                                                <Text style={{ 
                                                    fontSize: '13px',
                                                    color: index === 0 ? '#52c41a' : '#666'
                                                }}>
                                                    {routeEntry}
                                                </Text>
                                                {index === 0 && (
                                                    <Tag color="green" style={{ marginLeft: 8 }}>
                                                        {t('sfExpress.latestUpdate')}
                                                    </Tag>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </Descriptions.Item>
                            )}

                            {routeInfo.deliveryAddress && (
                                <Descriptions.Item
                                    label={
                                        <Space>
                                            <EnvironmentOutlined />
                                            {t('sfExpress.deliveryAddress')}
                                        </Space>
                                    }
                                >
                                    <Text>{routeInfo.deliveryAddress}</Text>
                                </Descriptions.Item>
                            )}

                            {routeInfo.senderAddress && (
                                <Descriptions.Item
                                    label={
                                        <Space>
                                            <EnvironmentOutlined />
                                            {t('sfExpress.senderAddress')}
                                        </Space>
                                    }
                                >
                                    <Text>{routeInfo.senderAddress}</Text>
                                </Descriptions.Item>
                            )}
                        </Descriptions>
                    </div>
                ) : (
                    <div style={{ textAlign: 'center', padding: '40px' }}>
                        <Alert
                            message={t('sfExpress.noRouteInfo')}
                            description={t('sfExpress.noRouteInfoDesc')}
                            type="info"
                            showIcon
                        />
                    </div>
                )}
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal
                title={
                    <Space>
                        <DeleteOutlined />
                        {t('sfExpress.deleteOrderTitle')}
                    </Space>
                }
                open={deleteModalVisible}
                onOk={handleConfirmDelete}
                onCancel={handleCancelDelete}
                confirmLoading={deleteLoading}
                okText={t('common.delete')}
                cancelText={t('common.cancel')}
                okButtonProps={{ danger: true }}
                destroyOnClose
            >
                <div>
                    <p>{t('sfExpress.deleteOrderConfirm')}</p>
                    <p><strong>{t('sfExpress.waybillNo')}:</strong> {latestOrder?.waybill_no}</p>
                    {latestOrder?.order_id && (
                        <p><strong>{t('sfExpress.orderId')}:</strong> {latestOrder.order_id}</p>
                    )}
                    <div style={{ marginTop: 16 }}>
                        <label>{t('sfExpress.deleteReason')}:</label>
                        <TextArea
                            rows={3}
                            value={deleteReason}
                            onChange={(e) => setDeleteReason(e.target.value)}
                            placeholder={t('sfExpress.deleteReasonPlaceholder')}
                            style={{ marginTop: 8 }}
                        />
                    </div>
                </div>
            </Modal>
        </>
    );
};

export default SfExpressCardSeparate;
